import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

/**
 * Header component that provides the main navigation for the application.
 * 
 * Features:
 * - Responsive layout with flexbox
 * - Brand logo and title
 * - Navigation links with icons
 * - Smooth hover transitions
 * - Accessible link elements
 * 
 * @component
 * @example
 * return (
 *   <Header />
 * )
 */
const Header = ({ onSearchChange }) => {
  return (
    /**
     * Main header element with background and shadow styling
     * Role is set to 'banner' for accessibility
     */
    <header className="bg-card shadow-card" role="banner">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        {/* Branding section with logo and title */}
        <Link 
          to="/" 
          className="flex items-center gap-2 font-semibold"
          aria-label="Go to homepage"
          onClick={() => {
            if (typeof onSearchChange === 'function') {
              onSearchChange({ target: { value: '' } });
            }
          }}
        >
          <div className="icon-container bot">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-bot">
              <path d="M12 8V4H8"></path>
              <rect width="16" height="12" x="4" y="8" rx="2"></rect>
              <path d="M2 14h2"></path>
              <path d="M20 14h2"></path>
              <path d="M15 13v2"></path>
              <path d="M9 13v2"></path>
            </svg>
          </div>
          <span>iReview.ai</span>
        </Link>
        {/* Search input */}
        <div className="flex-1 mx-4 max-w-md">
          <input
            type="text"
            placeholder="Search reviews of AI tools, products & services"
            onChange={(e) => onSearchChange(e)}
            className="w-full px-4 py-2 rounded-lg border border-border focus:ring-primary focus:border-primary transition-all"
          />
        </div>

        {/* Main navigation menu */}
        <nav 
          className="flex gap-6"
          aria-label="Main navigation"
        >
          {/* Home navigation link */}
          <Link 
            to="/" 
            className="text-muted hover:text-text transition-colors"
            aria-current="page"
            onClick={() => {
              if (typeof onSearchChange === 'function') {
                onSearchChange({ target: { value: '' } });
              }
            }}
          >
            Home
          </Link>
          {/* Featured products navigation link */}
          <Link 
            to="/featured" 
            className="text-muted hover:text-text transition-colors flex items-center gap-1"
            aria-label="View featured products"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Featured
          </Link>
          {/* Top products navigation link */}
          <Link 
            to="/top" 
            className="text-muted hover:text-text transition-colors flex items-center gap-1"
            aria-label="View top products"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trending-up h-5 w-5">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
            Top
          </Link>
        </nav>
      </div>
    </header>
  );
};

Header.propTypes = {
  onSearchChange: PropTypes.func
};

export default Header;
